import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  FaMoneyBillWave, FaUsers, FaUserGraduate, FaChartLine,
  FaCalendarCheck, FaExclamationTriangle, FaChalkboardTeacher,
  FaUserCheck, FaUserTimes, FaWallet, FaCreditCard, FaChartBar
} from 'react-icons/fa';
import { format, startOfMonth, subMonths } from 'date-fns';
import Notification from '../common/Notification';
import '../../styles/Dashboard.css';
import DashboardOverview from './DashboardOverview';
import DashboardFinancial from './DashboardFinancial';
import DashboardStudents from './DashboardStudents';
import DashboardAttendance from './DashboardAttendance';
import {
  fetchDashboardFinancialStats,
  fetchRevenueHistory,
  fetchReconciliationHistory,
  fetchClassWiseFeeCollection,
  fetchDailyCollectionForMonth,
  fetchTopDefaulters,
  fetchDashboardStudentStats,
  fetchNewAdmissions,
  fetchTodayAttendance,
  fetchTodayTeacherAttendance,
  fetchAttendanceTrend,
  fetchLowAttendanceStudents
} from '../../utils/supabaseClient';

function Dashboard() {
  // State for all data
  const [financialStats, setFinancialStats] = useState(null);
  const [revenueHistory, setRevenueHistory] = useState([]);
  const [reconciliationHistory, setReconciliationHistory] = useState([]);
  const [classWiseCollection, setClassWiseCollection] = useState([]);
  const [dailyCollection, setDailyCollection] = useState([]);
  const [topDefaulters, setTopDefaulters] = useState([]);
  const [studentStats, setStudentStats] = useState(null);
  const [newAdmissions, setNewAdmissions] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [teacherAttendance, setTeacherAttendance] = useState(null);
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [lowAttendanceStudents, setLowAttendanceStudents] = useState([]);
  
  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [activeTab, setActiveTab] = useState('overview'); // overview, financial, students, attendance
  
  // Filter states
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [attendanceTrendDays, setAttendanceTrendDays] = useState(7); // Default to 7 days
  const [financialActiveFilter, setFinancialActiveFilter] = useState('active');
  const [financialFilterLoading, setFinancialFilterLoading] = useState(false);

  // Fetch all data on component mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Fetch all data in parallel
      const [
        financialData,
        revenueData,
        reconciliationData,
        classCollectionData,
        dailyCollectionData,
        defaultersData,
        studentData,
        admissionsData,
        todayAttData,
        teacherAttData,
        attTrendData,
        lowAttData
      ] = await Promise.all([
        fetchDashboardFinancialStats(financialActiveFilter),
        fetchRevenueHistory(),
        fetchReconciliationHistory(),
        fetchClassWiseFeeCollection(selectedMonth, selectedYear, financialActiveFilter),
        fetchDailyCollectionForMonth(selectedMonth, selectedYear, financialActiveFilter),
        fetchTopDefaulters(10, financialActiveFilter),
        fetchDashboardStudentStats(),
        fetchNewAdmissions(7),
        fetchTodayAttendance(),
        fetchTodayTeacherAttendance(),
        fetchAttendanceTrend(attendanceTrendDays),
        fetchLowAttendanceStudents(75)
      ]);

      setFinancialStats(financialData);
      
      // Format revenue history for chart (12 months academic session)
      const revenueChartData = Object.entries(revenueData).map(([month, amount]) => ({
        month: format(new Date(month + '-01'), 'MMM yyyy'),
        revenue: amount
      }));
      setRevenueHistory(revenueChartData);
      
      // Format reconciliation history for chart (revenue, expenditure, students)
      const reconciliationChartData = Object.entries(reconciliationData).map(([month, data]) => ({
        month: format(new Date(month + '-01'), 'MMM yyyy'),
        revenue: data.revenue || 0,
        expenditure: data.expenditure || 0,
        students: data.students || 0
      }));
      setReconciliationHistory(reconciliationChartData);
      
      // Format daily collection for chart
      const dailyChartData = dailyCollectionData.map(item => ({
        day: item.day,
        collection: item.collection
      }));
      setDailyCollection(dailyChartData);

      // Format class-wise collection for chart
      const classCollectionChartData = Object.entries(classCollectionData)
        .map(([className, amount]) => ({
          class: className,
          amount: amount
        }))
        .sort((a, b) => {
          const order = ['NUR', 'KG1', 'KG2', 'STD1', 'STD2', 'STD3', 'STD4', 'STD5', 'STD6', 'STD7', 'STD8', 'STD9', 'STD10', 'STD11', 'STD12'];
          return order.indexOf(a.class) - order.indexOf(b.class);
        });
      setClassWiseCollection(classCollectionChartData);

      setTopDefaulters(defaultersData);
      setStudentStats(studentData);
      setNewAdmissions(admissionsData);
      setTodayAttendance(todayAttData);
      setTeacherAttendance(teacherAttData);
      
      // Format attendance trend for chart
      const attTrendChartData = attTrendData.map(item => ({
        date: format(new Date(item.date), attendanceTrendDays <= 30 ? 'MMM dd' : 'MMM dd, yy'),
        rate: parseFloat(item.rate)
      }));
      setAttendanceTrend(attTrendChartData);
      
      setLowAttendanceStudents(lowAttData.slice(0, 10));

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showNotification('Failed to load dashboard data. Please refresh.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
  };

  const closeNotification = () => {
    setNotification({ show: false, message: '', type: 'success' });
  };
  
  // Handle month/year change for filters
  const handleMonthYearChange = async (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    
    try {
      // Fetch updated data for new month/year
      const [classCollectionData, dailyCollectionData] = await Promise.all([
        fetchClassWiseFeeCollection(month, year, financialActiveFilter),
        fetchDailyCollectionForMonth(month, year, financialActiveFilter)
      ]);
      
      // Format class-wise collection
      const classCollectionChartData = Object.entries(classCollectionData)
        .map(([className, amount]) => ({
          class: className,
          amount: amount
        }))
        .sort((a, b) => {
          const order = ['NUR', 'KG1', 'KG2', 'STD1', 'STD2', 'STD3', 'STD4', 'STD5', 'STD6', 'STD7', 'STD8', 'STD9', 'STD10', 'STD11', 'STD12'];
          return order.indexOf(a.class) - order.indexOf(b.class);
        });
      setClassWiseCollection(classCollectionChartData);
      
      // Format daily collection
      const dailyChartData = dailyCollectionData.map(item => ({
        day: item.day,
        collection: item.collection
      }));
      setDailyCollection(dailyChartData);
    } catch (error) {
      console.error('Error updating month data:', error);
      showNotification('Failed to update data for selected month', 'error');
    }
  };
  
  // Handle active filter change for financial tab
  const handleFinancialActiveFilterChange = async (filter) => {
    setFinancialActiveFilter(filter);
    setFinancialFilterLoading(true);
    try {
      const [financialData, classCollectionData, dailyCollectionData, defaultersData] = await Promise.all([
        fetchDashboardFinancialStats(filter),
        fetchClassWiseFeeCollection(selectedMonth, selectedYear, filter),
        fetchDailyCollectionForMonth(selectedMonth, selectedYear, filter),
        fetchTopDefaulters(10, filter)
      ]);

      setFinancialStats(financialData);

      const classCollectionChartData = Object.entries(classCollectionData)
        .map(([className, amount]) => ({ class: className, amount }))
        .sort((a, b) => {
          const order = ['NUR', 'KG1', 'KG2', 'STD1', 'STD2', 'STD3', 'STD4', 'STD5', 'STD6', 'STD7', 'STD8', 'STD9', 'STD10', 'STD11', 'STD12'];
          return order.indexOf(a.class) - order.indexOf(b.class);
        });
      setClassWiseCollection(classCollectionChartData);

      const dailyChartData = dailyCollectionData.map(item => ({ day: item.day, collection: item.collection }));
      setDailyCollection(dailyChartData);
      setTopDefaulters(defaultersData);
    } catch (error) {
      console.error('Error updating financial active filter:', error);
      showNotification('Failed to update data for selected filter', 'error');
    } finally {
      setFinancialFilterLoading(false);
    }
  };

  // Handle attendance trend time range change
  const handleAttendanceTrendRangeChange = async (days) => {
    setAttendanceTrendDays(days);
    
    try {
      const attTrendData = await fetchAttendanceTrend(days);
      
      // Format attendance trend for chart
      const attTrendChartData = attTrendData.map(item => ({
        date: format(new Date(item.date), days <= 30 ? 'MMM dd' : 'MMM dd, yy'),
        rate: parseFloat(item.rate)
      }));
      setAttendanceTrend(attTrendChartData);
    } catch (error) {
      console.error('Error updating attendance trend:', error);
      showNotification('Failed to update attendance trend', 'error');
    }
  };

  // Chart colors
  const COLORS = ['#3498db', '#2ecc71', '#f39c12', '#e74c3c', '#9b59b6', '#1abc9c'];

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content-dashboard">
          <h1>Dashboard</h1>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <div className="tabs-left">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FaChartLine /> Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'financial' ? 'active' : ''}`}
            onClick={() => setActiveTab('financial')}
          >
            <FaMoneyBillWave /> Financial
          </button>
          <button 
            className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            <FaUserGraduate /> Students
          </button>
          <button 
            className={`tab-button ${activeTab === 'attendance' ? 'active' : ''}`}
            onClick={() => setActiveTab('attendance')}
          >
            <FaCalendarCheck /> Attendance
          </button>
        </div>
        {activeTab === 'financial' && (
          <div className="tabs-right-filters">
            <select 
              value={selectedMonth} 
              onChange={e => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              className="month-filter"
            >
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
            <select 
              value={selectedYear} 
              onChange={e => handleMonthYearChange(selectedMonth, parseInt(e.target.value))}
              className="year-filter"
            >
              <option value={today.getFullYear() - 1}>{today.getFullYear() - 1}</option>
              <option value={today.getFullYear()}>{today.getFullYear()}</option>
              <option value={today.getFullYear() + 1}>{today.getFullYear() + 1}</option>
            </select>
            <select
              value={financialActiveFilter}
              onChange={e => handleFinancialActiveFilterChange(e.target.value)}
              className="active-status-filter"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </div>
        )}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <DashboardOverview
          financialStats={financialStats}
          studentStats={studentStats}
          todayAttendance={todayAttendance}
          teacherAttendance={teacherAttendance}
          revenueHistory={revenueHistory}
          reconciliationHistory={reconciliationHistory}
          attendanceTrend={attendanceTrend}
          topDefaulters={topDefaulters}
          lowAttendanceStudents={lowAttendanceStudents}
          formatCurrency={formatCurrency}
        />
      )}

      {/* FINANCIAL TAB */}
      {activeTab === 'financial' && (
        <DashboardFinancial
          financialStats={financialStats}
          revenueHistory={revenueHistory}
          reconciliationHistory={reconciliationHistory}
          classWiseCollection={classWiseCollection}
          dailyCollection={dailyCollection}
          topDefaulters={topDefaulters}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          handleMonthYearChange={handleMonthYearChange}
          formatCurrency={formatCurrency}
          COLORS={COLORS}
          today={today}
          filterLoading={financialFilterLoading}
        />
      )}

      {/* STUDENTS TAB */}
      {activeTab === 'students' && (
        <DashboardStudents
          studentStats={studentStats}
          newAdmissions={newAdmissions}
        />
      )}

      {/* ATTENDANCE TAB */}
      {activeTab === 'attendance' && (
        <DashboardAttendance
          todayAttendance={todayAttendance}
          teacherAttendance={teacherAttendance}
          attendanceTrend={attendanceTrend}
          attendanceTrendDays={attendanceTrendDays}
          handleAttendanceTrendRangeChange={handleAttendanceTrendRangeChange}
          lowAttendanceStudents={lowAttendanceStudents}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Notification */}
      {notification.show && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
    </div>
  );
}

export default Dashboard;
